package kz.dentalcrm.app.core.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kz.dentalcrm.app.theme.DefaultTheme
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.layout.offset
import androidx.compose.ui.Alignment
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.toArgb
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.window.Dialog
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.select
import dentalcrm.composeapp.generated.resources.select_pin_color
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.vectorResource
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.hypot
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.PI

@Stable
@Composable
fun UIColorInputClickable(
    modifier: Modifier,
    enabled: Boolean = true,
    borderWidth: Dp = 1.dp,

    isOnError: Boolean = false,
    errorText: String = "",
    errorIconId: DrawableResource? = null,

    leadingIcon: ImageVector? = null,
    leadingIconSize: Dp = 16.dp,

    trailingIcon: ImageVector? = null,
    trailingIconSize: Dp = 16.dp,
    trailingIconClicked: () -> Unit = { },

    label: String = "",
    hint: String = "",
    hintIconId: DrawableResource? = null,
    isOnlyNumbers: Boolean = false,
    maxLines: Int = 1,
    maxLength: Int? = null,
    defaultValue: String = "",
    cornerShape: Dp = 16.dp,
    readOnly: Boolean = true,
    singleLine: Boolean = true,
    backgroundColor: Color = DefaultTheme.colors.surfaceBackground,
    trimInput: Boolean = true,
    trailingActionClear: Boolean = true,
    keyboardType: KeyboardType = KeyboardType.Text,
    keyboardOptions: KeyboardOptions = KeyboardOptions(imeAction = ImeAction.Next).copy(
        keyboardType = keyboardType
    ),
    additionalPaddingValues: PaddingValues = PaddingValues(vertical = 4.dp, horizontal = 8.dp),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    visualTransformation: VisualTransformation? = null,
    valueChanged: (value: String) -> Unit,
    colorChanged: (value: String) -> Unit
) {

    var textState = defaultValue
    var selectedColor by remember(defaultValue) { mutableStateOf(parseColor(defaultValue)) }
    var colorSelectorDialog by rememberSaveable { mutableStateOf(false) }
    var isFocused by remember { mutableStateOf(false) }

    val onlyNumbers by remember { mutableStateOf(isOnlyNumbers) }

    val hintVisible by remember { mutableStateOf(hint.isNotEmpty()) }

    val labelStyle = when {
        isFocused -> DefaultTheme.typography.labelRegular
        textState.isNotEmpty() -> DefaultTheme.typography.labelRegular
        else -> DefaultTheme.typography.bodyMediumMedium
    }
    val borderColor = when {
        isOnError -> DefaultTheme.colors.error
        isFocused -> DefaultTheme.colors.primary
        else -> DefaultTheme.colors.outline
    }

    Column {
        Box(modifier = modifier) {
            TextField(
                modifier = modifier
                    .background(
                        color = backgroundColor,
                        shape = RoundedCornerShape(
                            cornerShape
                        )
                    )
                    .border(
                        width = borderWidth,
                        color = borderColor,
                        shape = RoundedCornerShape(
                            cornerShape
                        )
                    )
                    .focusable(enabled = false),
                colors = TextFieldDefaults.colors(
                    focusedContainerColor = Color.Transparent,
                    unfocusedContainerColor = Color.Transparent,
                    disabledContainerColor = Color.Transparent,
                    errorContainerColor = Color.Transparent,
                    errorLeadingIconColor = Color.Transparent,
                    errorTrailingIconColor = Color.Transparent,
                    errorIndicatorColor = Color.Transparent,
                    disabledIndicatorColor = Color.Transparent,
                    disabledLeadingIconColor = Color.Transparent,
                    disabledTrailingIconColor = Color.Transparent,
                    focusedIndicatorColor = Color.Transparent,
                    unfocusedIndicatorColor = Color.Transparent
                ),
                shape = RoundedCornerShape(cornerShape),
                textStyle = DefaultTheme.typography.bodyMediumMedium.copy(
                    color = DefaultTheme.colors.onSurface,
                ),
                leadingIcon = leadingIcon?.let {
                    {
                        Icon(
                            imageVector = leadingIcon,
                            contentDescription = null,
                            tint = borderColor,
                            modifier = Modifier.size(leadingIconSize)
                        )
                    }
                },
                label = if (label.isNotEmpty()) {
                    {
                        Text(
                            text = label,
                            style = labelStyle,
                            color = borderColor
                        )
                    }
                } else null,
                trailingIcon = {
                    Box(
                        modifier = Modifier
                            .size(24.dp)
                            .clip(
                                shape = RoundedCornerShape(
                                    cornerShape
                                )
                            )
                            .focusable(false)
                            .width(intrinsicSize = IntrinsicSize.Min)
                            .background(
                                color = selectedColor
                            )
                    )
                },
                visualTransformation = visualTransformation ?: VisualTransformation.None,
                enabled = enabled,
                value = textState,
                maxLines = maxLines,
                singleLine = singleLine,
                keyboardOptions = keyboardOptions,
                keyboardActions = keyboardActions,
                readOnly = readOnly,
                onValueChange = { valueChanged ->
                    val value = if (!trimInput) valueChanged.trim() else valueChanged

                    val res = if (onlyNumbers) {
                        value.filter { symbol -> symbol.isDigit() }
                    } else {
                        value
                    }
                    (maxLength?.let {
                        valueChanged(manageLength(res, it)).also {
                            textState = manageLength(res, maxLength)
                        }
                    } ?: run {
                        valueChanged(res)
                        textState = res
                    })
                },
            )
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .clip(
                        shape = RoundedCornerShape(
                            cornerShape
                        )
                    )
                    .focusable(true)
                    .clickable {
                        colorSelectorDialog = true
                    }
                    .width(intrinsicSize = IntrinsicSize.Min)
            )
        }

        AnimatedVisibility(visible = hintVisible) {
            Spacer(
                modifier = Modifier
                    .widthIn()
                    .height(12.dp)
            )
            Row(
                modifier = Modifier.padding(additionalPaddingValues)
            ) {
                hintIconId?.let {
                    Icon(
                        imageVector = vectorResource(hintIconId),
                        contentDescription = null
                    )
                }
                Spacer(
                    modifier = Modifier
                        .width(8.dp)
                )
                Text(
                    text = hint,
                    textAlign = TextAlign.Start,
                    style = DefaultTheme.typography.labelRegular,
                )
            }
        }
        AnimatedVisibility(visible = isOnError) {
            Spacer(
                modifier = Modifier
                    .widthIn()
                    .height(12.dp)
            )
            Row(
                modifier = Modifier.padding(additionalPaddingValues)
            ) {
                errorIconId?.let {
                    Icon(
                        imageVector = vectorResource(errorIconId),
                        contentDescription = null
                    )
                }
                Spacer(
                    modifier = Modifier
                        .width(8.dp)
                )
                Text(
                    text = errorText,
                    textAlign = TextAlign.Start,
                    color = DefaultTheme.colors.error,
                    style = DefaultTheme.typography.labelRegular,
                )
            }
        }
    }

    if (colorSelectorDialog) {
        ColorPickerDialog(
            initialColor = defaultValue,
            onDismiss = { colorSelectorDialog = false },
            onColorSelected = { hex ->
                selectedColor = parseColor(hex)
                colorChanged(hex)
                colorSelectorDialog = false
            }
        )
    }
}

private fun manageLength(input: String, length: Int) =
    if (input.length > length) input.substring(0, length) else input

@Composable
fun ColorPickerDialog(
    initialColor: String,
    onDismiss: () -> Unit,
    onColorSelected: (String) -> Unit
) {
    var selectedColor by remember { mutableStateOf(parseColor(initialColor)) }

    Dialog(onDismissRequest = onDismiss) {
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(16.dp))
                .background(DefaultTheme.colors.surfaceAlternative)
                .padding(16.dp)
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = stringResource(Res.string.select_pin_color),
                    style = DefaultTheme.typography.heading3,
                    color = DefaultTheme.colors.onSurface
                )
                Spacer(modifier = Modifier.height(16.dp))

                ColorWheel(
                    modifier = Modifier
                        .fillMaxWidth()
                        .aspectRatio(1f),
                    initialColor = selectedColor,
                    onColorChanged = { selectedColor = it }
                )

                Spacer(modifier = Modifier.height(24.dp))

                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(40.dp)
                            .clip(CircleShape)
                            .background(selectedColor)
                            .border(1.dp, DefaultTheme.colors.outline, CircleShape)
                    )
                    Spacer(modifier = Modifier.width(16.dp))
                    Text(
                        text = colorToHex(selectedColor),
                        style = DefaultTheme.typography.bodyLargeMedium,
                        color = DefaultTheme.colors.onSurface
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                UIButton(
                    modifier = Modifier.fillMaxWidth(),
                    text = stringResource(Res.string.select),
                    onClick = { onColorSelected(colorToHex(selectedColor)) }
                )
            }
        }
    }
}

@Composable
fun ColorWheel(
    modifier: Modifier = Modifier,
    initialColor: Color,
    onColorChanged: (Color) -> Unit
) {
    val density = LocalDensity.current
    var center by remember { mutableStateOf(Offset.Zero) }
    var radius by remember { mutableStateOf(0f) }
    var selectorPosition by remember { mutableStateOf(Offset.Zero) }

    Box(
        modifier = modifier
            .onGloballyPositioned {
                val size = it.size
                center = Offset(size.width / 2f, size.height / 2f)
                radius = size.width.coerceAtMost(size.height) / 2f
                
                if (selectorPosition == Offset.Zero) {
                     selectorPosition = center
                }
            }
            .pointerInput(Unit) {
                detectDragGestures(
                    onDragStart = { offset ->
                        val newPos = limitPosition(offset, center, radius)
                        selectorPosition = newPos
                        onColorChanged(getColorAtPosition(newPos, center, radius))
                    },
                    onDrag = { change, _ ->
                        val newPos = limitPosition(change.position, center, radius)
                        selectorPosition = newPos
                        onColorChanged(getColorAtPosition(newPos, center, radius))
                    }
                )
            }
    ) {
        Canvas(modifier = Modifier.matchParentSize()) {
            val sweepGradient = Brush.sweepGradient(
                colors = listOf(
                    Color.Red,
                    Color.Yellow,
                    Color.Green,
                    Color.Cyan,
                    Color.Blue,
                    Color.Magenta,
                    Color.Red
                ),
                center = center
            )
            
            drawCircle(
                brush = sweepGradient,
                radius = radius,
                center = center
            )
            
            drawCircle(
                brush = Brush.radialGradient(
                    colors = listOf(Color.White, Color.Transparent),
                    center = center,
                    radius = radius
                ),
                radius = radius,
                center = center
            )
        }

        Box(
            modifier = Modifier
                .offset { 
                    with(density) {
                        IntOffset(selectorPosition.x.roundToInt() - 10.dp.roundToPx(), selectorPosition.y.roundToInt() - 10.dp.roundToPx()) 
                    }
                }
                .size(20.dp)
                .clip(CircleShape)
                .background(Color.White)
                .border(2.dp, Color.Black, CircleShape)
        )
    }
}

private fun limitPosition(pos: Offset, center: Offset, radius: Float): Offset {
    val distance = hypot(pos.x - center.x, pos.y - center.y)
    return if (distance <= radius) {
        pos
    } else {
        val angle = atan2(pos.y - center.y, pos.x - center.x)
        Offset(
            center.x + cos(angle) * radius,
            center.y + sin(angle) * radius
        )
    }
}

private fun getColorAtPosition(pos: Offset, center: Offset, radius: Float): Color {
    val dx = pos.x - center.x
    val dy = pos.y - center.y
    val distance = hypot(dx, dy)
    val saturation = (distance / radius).coerceIn(0f, 1f)
    
    val angle = ((atan2(dy.toDouble(), dx.toDouble()) * 180 / PI) + 360) % 360
    val hue = angle.toFloat()

    return Color.hsv(hue, saturation, 1f)
}

fun parseColor(hex: String): Color {
    return try {
        if (hex.startsWith("#")) {
             Color(hex.substring(1).toLong(16) or 0xFF000000)
        } else {
             Color(hex.toLong(16) or 0xFF000000)
        }
    } catch (e: Exception) {
        Color.White
    }
}

fun colorToHex(color: Color): String {
    val argb = color.toArgb()
    val red = (argb shr 16) and 0xFF
    val green = (argb shr 8) and 0xFF
    val blue = argb and 0xFF
    
    fun Int.toHex2(): String {
        val hex = this.toString(16)
        return if (hex.length == 1) "0$hex" else hex
    }
    
    return "#${red.toHex2()}${green.toHex2()}${blue.toHex2()}".uppercase()
}
